import React, { useEffect, useState } from "react";
import { listUserTeams, attachTeamToLeague } from "../lib/api";

/**
 * AttachTeamPicker — fixes the Phase 60 "I can't bring my own team
 * into my own league" UX gap.
 *
 * Behaviour:
 *   • Reads the current user's teams.
 *   • Filters to STANDALONE teams (no league_id) where the user is
 *     the captain AND the team supports `leagueGame`.
 *   • Renders a row per attachable team with an ATTACH button.
 *   • Hides itself entirely when no attachable team exists — keeps
 *     the modal clean for users who only need the CREATE flow.
 */
export function AttachTeamPicker({ user, league, onToast, onAttached }) {
  const [teams, setTeams] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.user_id || !league?.league_id) return;
      try {
        const res = await listUserTeams(user.user_id);
        if (cancelled) return;
        const matchGame = (league.game || "").toLowerCase();
        const filtered = (res || []).filter((t) => {
          if (t.league_id) return false;
          if (t.captain_user_id !== user.user_id) return false;
          const games = (t.supported_games || []).map((g) => (g || "").toLowerCase());
          // Either it explicitly supports this game OR it has no game
          // tag yet (legacy unannotated teams) — let the backend make
          // the final call.
          return games.length === 0 || games.includes(matchGame);
        });
        setTeams(filtered);
      } catch (_e) { /* 401 silent */ }
    })();
    return () => { cancelled = true; };
  }, [user?.user_id, league?.league_id, league?.game]);

  if (!teams.length) return null;

  const attach = async (team) => {
    if (!window.confirm(`Attach "${team.name}" to ${league.name}? You'll join the league as captain of the team.`)) return;
    setBusy(team.team_id);
    try {
      await attachTeamToLeague(team.team_id, league.league_id);
      onToast?.(`🛡 "${team.name}" joined ${league.name}.`);
      setTeams((prev) => prev.filter((t) => t.team_id !== team.team_id));
      onAttached?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't attach.");
    } finally { setBusy(""); }
  };

  return (
    <div
      data-testid="attach-team-picker"
      className="mb-3 glass rounded-2xl p-3 border border-[#14b8a6]/35"
    >
      <p className="text-[10px] tracking-[3px] font-bold text-[#14b8a6] mb-2">
        <i className="fas fa-link mr-1.5" />BRING AN EXISTING TEAM
      </p>
      <p className="text-[11px] text-white/55 leading-snug mb-2">
        You're the captain of {teams.length === 1 ? "this team" : `${teams.length} standalone teams`}
        — attach to join this league instantly.
      </p>
      <div className="space-y-2">
        {teams.map((t) => (
          <div
            key={t.team_id}
            data-testid={`attach-team-row-${t.team_id}`}
            className="glass rounded-xl p-2.5 flex items-center gap-3 border border-white/10"
          >
            <div className="w-9 h-9 rounded-xl bg-black/40 border border-white/15 flex items-center justify-center shrink-0 text-[#14b8a6]">
              <i className="fas fa-shield-halved" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white truncate">{t.name}</p>
              <p className="text-[10px] text-white/45 truncate">
                MMR {t.mmr || 1500} · {(t.member_user_ids || []).length}/{t.max_members || 8} members
              </p>
            </div>
            <button
              type="button"
              onClick={() => attach(t)}
              disabled={busy === t.team_id}
              data-testid={`attach-team-btn-${t.team_id}`}
              className="text-[11px] tracking-widest px-3 py-1.5 rounded-xl btn-neon disabled:opacity-40"
            >
              {busy === t.team_id ? "..." : "ATTACH"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default AttachTeamPicker;
